import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useAtom } from 'jotai';
import { ArrowUpRightIcon, ChevronDown, CirclePlus, Search, X, HomeIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CreateEstateModal } from '@/components/modals/create-estate-modal';
import { useModal } from '@/components/modals/new/use-modal';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { type Estate, getEstates } from '@/features/estates/api/estates';
import { getEstate } from '@/features/estates-property/api/estate';
import {
  getProject,
  getProjectsQueryOptions,
  type Projects,
} from '@/features/projects/api/projects';
import { getSearchAll, type SearchAllResult } from '@/features/search/api/search';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback';
import { cn } from '@/lib/utils';

import { renderingsEstateIdAtom, renderingsProjectIdAtom } from '../atoms/relation';

type Project = Projects[number];
type SearchEstate = NonNullable<SearchAllResult['estates']>[number];
type SearchProject = NonNullable<SearchAllResult['projects']>[number];
type ObjectItem = Estate | Project | SearchEstate | SearchProject;

const getObjectLabel = (obj: ObjectItem) =>
  obj.name ?? ('address' in obj ? obj.address?.address : '');

export const SourceObjectCard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const modal = useModal();

  const [estateId, setEstateId] = useAtom(renderingsEstateIdAtom);
  const [projectId, setProjectId] = useAtom(renderingsProjectIdAtom);

  const [isOpen, setIsOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);
  const [searchString, setSearchString] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [items, setItems] = useState<ObjectItem[]>([]);
  const loadRequestIdRef = useRef(0);

  const fetchSelectedLabel = useCallback(async () => {
    if (estateId) {
      try {
        const estate = await getEstate(estateId);
        setSelectedLabel(estate.name ?? estate.address?.address ?? null);
      } catch {
        setSelectedLabel(null);
      }
      return;
    }

    if (projectId) {
      try {
        const project = await getProject(projectId);
        setSelectedLabel(project.name ?? null);
      } catch {
        setSelectedLabel(null);
      }
      return;
    }

    setSelectedLabel(null);
  }, [estateId, projectId]);

  useEffect(() => {
    fetchSelectedLabel();
  }, [fetchSelectedLabel]);

  const triggerText = selectedLabel ?? t('renderings.select-estate-or-project');
  const hasSelection = Boolean(selectedLabel);

  const {
    data: projects,
    isLoading: isProjectsLoading,
    refetch,
  } = useQuery(
    getProjectsQueryOptions({
      query: {
        page: 1,
        pageSize: 999,
      },
    })
  );

  const loadItemsImpl = useCallback(async (query: string, availableProjects: Project[]) => {
    const requestId = loadRequestIdRef.current + 1;
    loadRequestIdRef.current = requestId;

    try {
      setLoading(true);
      setLoadError(false);

      if (query) {
        const results = (await getSearchAll({
          query: {
            searchType: 'all',
            page: 1,
            pageSize: 999,
            filters: {
              searchString: query,
            },
          },
        })) as SearchAllResult;

        if (loadRequestIdRef.current !== requestId) return;

        setItems([...(results.estates ?? []), ...(results.projects ?? [])]);
        return;
      }

      const estates = await getEstates({
        query: {},
      });

      if (loadRequestIdRef.current !== requestId) return;

      setItems([...estates, ...availableProjects]);
    } catch {
      if (loadRequestIdRef.current === requestId) {
        setItems([]);
        setLoadError(true);
      }
    } finally {
      if (loadRequestIdRef.current === requestId) {
        setLoading(false);
      }
    }
  }, []);

  const loadItems = useDebouncedCallback(loadItemsImpl, 250);
  const normalizedSearchString = searchString.trim();

  useEffect(() => {
    if (!isOpen) {
      loadItems.cancel();
      return;
    }
    loadItems(normalizedSearchString, projects ?? []);

    return () => loadItems.cancel();
  }, [isOpen, loadItems, normalizedSearchString, projects]);

  const triggerClassName = useMemo(() => {
    return cn(
      'group flex min-w-0 items-center justify-start gap-2 whitespace-normal border-0 bg-transparent px-3 py-0 text-left text-[12px] text-inherit hover:bg-transparent hover:text-inherit hover:opacity-100 focus-visible:outline-none',
      hasSelection ? 'min-h-12 flex-1' : 'h-8 w-full flex-none sm:w-[261px]'
    );
  }, [hasSelection]);

  const objectFieldClassName = cn(
    'flex w-full items-center rounded-sm border border-slate-200 bg-white text-slate-500 transition-colors sm:w-[261px]',
    hasSelection ? 'min-h-12' : 'h-8',
    isOpen
      ? 'bg-primary-light text-white'
      : 'hover:bg-primary-light hover:text-white hover:opacity-90'
  );

  const handleSelect = (obj: ObjectItem) => {
    if ('estateDetails' in obj) {
      setProjectId(undefined);
      setEstateId(obj.id);
      setSelectedLabel(obj.name ?? obj.address?.address ?? null);
    } else {
      setEstateId(undefined);
      setProjectId(obj.id);
      setSelectedLabel(obj.name ?? null);
    }
    setIsOpen(false);
  };

  const handleClear = () => {
    setEstateId(undefined);
    setProjectId(undefined);
    setSelectedLabel(null);
    setIsOpen(false);
  };

  return (
    <Accordion
      type="single"
      defaultValue="object"
      collapsible
      className="w-full overflow-hidden rounded-sm border border-slate-200 bg-white sm:w-[287px]"
    >
      <AccordionItem value="object" className="overflow-hidden data-[state=open]:min-h-[82px]">
        <AccordionTrigger
          arrowIcon={
            <ChevronDown
              aria-hidden
              className="size-4 shrink-0 text-slate-500 transition-transform duration-200"
            />
          }
          className="h-9 px-3 py-0 text-sm font-semibold text-slate-500"
        >
          <span className="shrink-0 text-left">
            {t('object')}
            <span aria-hidden>*</span>
          </span>
          {selectedLabel ? (
            <span className="min-w-0 flex-1 truncate text-left text-xs font-semibold text-primary-light">
              <HomeIcon aria-hidden className="size-3" />
            </span>
          ) : (
            <span className="flex-1" />
          )}
        </AccordionTrigger>

        <AccordionContent className="px-3 pb-3 pt-0">
          <div className={objectFieldClassName}>
            <Popover open={isOpen} onOpenChange={setIsOpen}>
              <PopoverTrigger asChild>
                <Button type="button" variant="ghost" className={triggerClassName}>
                  {!hasSelection ? (
                    <Search
                      aria-hidden
                      className={cn(
                        'size-5 shrink-0 transition-colors hover:text-white group-hover:text-white',
                        isOpen ? 'text-white' : 'text-inherit'
                      )}
                    />
                  ) : null}
                  <span className="line-clamp-2 min-w-0 flex-1 whitespace-normal break-words text-sm font-semibold leading-4">
                    {triggerText}
                  </span>
                </Button>
              </PopoverTrigger>

              <PopoverContent
                align="start"
                side="bottom"
                sideOffset={8}
                avoidCollisions={false}
                className="w-[--radix-popover-trigger-width] overflow-hidden rounded-sm border border-slate-200 bg-white p-0 shadow-lg"
              >
                <div className="p-2">
                  <Input
                    size="sm"
                    placeholder={t('renderings.search-estates-and-projects')}
                    value={searchString}
                    onChange={(e) => setSearchString(e.target.value)}
                    className="border border-primary-light"
                    startIcon={<Search aria-hidden className="text-slate-500" />}
                  />
                </div>

                <div className="max-h-64 overflow-auto border-t border-slate-100">
                  {loading || isProjectsLoading ? (
                    <div className="p-4 text-sm text-slate-500">{t('loading')}</div>
                  ) : loadError ? (
                    <div className="p-4 text-sm text-red-600">
                      {t('renderings.load-objects-error', 'Could not load objects')}
                    </div>
                  ) : items.length === 0 ? (
                    <div className="p-4 text-sm text-slate-500">{t('noObjects')}</div>
                  ) : (
                    items.map((obj) => (
                      <button
                        key={obj.id}
                        type="button"
                        onClick={() => handleSelect(obj)}
                        className="flex w-full items-center px-3 py-0.5 text-left hover:bg-primary-light/10"
                      >
                        <span className="truncate text-sm font-semibold text-slate-700">
                          {getObjectLabel(obj)}
                        </span>
                      </button>
                    ))
                  )}
                </div>

                <div className="border-t border-slate-100 p-2">
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full justify-start gap-2 rounded-lg px-2 py-2 text-sm font-semibold text-primary-light hover:bg-primary-light/10"
                    onClick={() => modal.open()}
                  >
                    <CirclePlus aria-hidden className="size-4" />
                    <span>{t('renderings.create-new')}</span>
                  </Button>
                </div>
              </PopoverContent>
            </Popover>

            {estateId ? (
              <Link
                to="/estates/$estateId"
                params={{ estateId }}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-sm text-inherit transition-colors hover:opacity-80 [&_svg]:size-5"
                target="_blank"
                aria-label={t('open-selected-object')}
              >
                <ArrowUpRightIcon aria-hidden />
              </Link>
            ) : projectId ? (
              <Link
                to="/projects/$projectId"
                params={{ projectId }}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-sm text-inherit transition-colors hover:opacity-80 [&_svg]:size-5"
                target="_blank"
                aria-label={t('open-selected-object')}
              >
                <ArrowUpRightIcon aria-hidden />
              </Link>
            ) : null}

            {hasSelection ? (
              <Button
                type="button"
                variant="ghost"
                onClick={handleClear}
                aria-label={t('clear')}
                className="mr-1 h-8 w-8 shrink-0 rounded-sm p-0 text-inherit hover:bg-primary-light hover:text-white hover:opacity-90 [&_svg]:size-4"
              >
                <X aria-hidden />
              </Button>
            ) : null}
          </div>

          <CreateEstateModal
            modal={modal}
            navigate={navigate}
            onSuccess={(obj) => {
              if (!obj) return;
              if ('estateId' in obj && obj.estateId) {
                setEstateId(obj.estateId);
                setProjectId(undefined);
              }
              if ('projectId' in obj && obj.projectId) {
                setProjectId(obj.projectId);
                setEstateId(undefined);
                refetch();
              }
            }}
          />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
